// src/services/auth.service.ts
import { apiClient } from "./api/axios.config";
import { handleApiError } from "../utils/commonApiError";
import {
  AuthResponse,
  LoginCredentials,
  RegistrationData,
} from "../types/auth";
import { secureStoreService } from "./secure-store.service";

export type AuthStatus = {
  token: string | null;
  phVerStatus: string | null;
};

// Common user properties
export interface AuthResCommon {
  message: string;
  accessToken: string;
  refreshToken: string;
  isPhoneVerified: boolean;
}

interface AuthError {
  message: string;
  status?: number;
}

export class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    console.log("[AuthService] Attempting login...");

    try {
      console.log("[AuthService] Calling login API...");
      const response = await apiClient.post<AuthResponse>(
        "/auth/login",
        credentials
      );

      console.log("[AuthService] Login successful, storing tokens...");
      await this.storeAuthData(
        response.data.accessToken,
        response.data.refreshToken,
        response.data.isPhoneVerified.toString()
      );

      console.log("[AuthService] Login process completed");
      return response.data;
    } catch (error) {
      console.error("[AuthService] Login failed:", error);
      throw handleApiError(error);
    }
  }

  async register(data: RegistrationData): Promise<AuthResponse> {
    console.log("[AuthService] Attempting registration...");

    try {
      console.log("[AuthService] Calling registration API...");
      const response = await apiClient.post<AuthResponse>(
        "/auth/register",
        data
      );

      console.log("[AuthService] Registration successful, storing tokens...");
      await this.storeAuthData(
        response.data.accessToken,
        response.data.refreshToken,
        response.data.isPhoneVerified.toString()
      );

      console.log("[AuthService] Registration process completed");
      return response.data;
    } catch (error) {
      console.error("[AuthService] Registration failed:", error);
      throw handleAuthError(error);
    }
  }

  private async storeAuthData(
    accessToken: string,
    refreshToken: string,
    isPhVerified: string
  ) {
    await Promise.all([
      secureStoreService.setAccessToken(accessToken),
      secureStoreService.setRefreshToken(refreshToken),
      secureStoreService.setPhoneVerificationStatus(isPhVerified),
    ]);
  }

  async updateTokens(authToken: string, refreshToken: string): Promise<void> {
    console.log("[VerificationService] Updating stored tokens...");
    try {
      await secureStoreService.setAccessToken(authToken);
      await secureStoreService.setRefreshToken(refreshToken);
      await secureStoreService.setPhoneVerificationStatus("true");
      console.log("[VerificationService] Tokens updated successfully");
    } catch (error) {
      console.error("[VerificationService] Failed to update tokens:", error);
      throw new Error(
        "Failed to store authentication tokens after verification"
      );
    }
  }

  logout = async (): Promise<void> => {
    console.log("[AuthService] Performing logout...");
    try {
      await secureStoreService.clearAll();
      console.log("[AuthService] Logout completed, tokens removed");
    } catch (error) {
      console.error("[AuthService] Logout failed:", error);
      throw new Error("Failed to complete logout");
    }
  };
}

export const authService = new AuthService();

//TODO: Add API for logout

export const handleAuthError = (error: any): AuthError => {
  console.log("[AuthService] Handling error...", error);

  if (error.response) {
    const errorResponse = {
      message: error.response.data.message || "An error occurred",
      status: error.response.status,
    };
    console.log("[AuthService] Formatted error response:", errorResponse);
    return errorResponse;
  }

  const networkError = {
    message: "Network error occurred",
    status: 500,
  };
  console.log("[AuthService] Network error formatted:", networkError);
  return networkError;
};
